// App wrapper - handles auth, WebSocket, and initial data fetching
import { useEffect, Component, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { api } from './services/api';
import { useTradingStore } from './stores/tradingStore';
import { useAcesStore } from './stores/acesStore';
import { useAuth } from './hooks/useAuth';
import { AuthModal } from './components/Auth';
import { useWebSocket } from './hooks/useWebSocket';

// Error Boundary to catch any render errors
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{children: ReactNode}, ErrorBoundaryState> {
  constructor(props: {children: ReactNode}) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', background: '#F8F6F1', color: '#1A1814', minHeight: '100vh' }}>
          <h1 style={{ color: '#C43D3D' }}>Dashboard Error</h1>
          <p>Something went wrong:</p>
          <pre style={{ background: '#FFFFFF', padding: '20px', borderRadius: '8px', overflow: 'auto', border: '1px solid rgba(0,0,0,0.06)' }}>
            {this.state.error?.message}
          </pre>
          <pre style={{ background: '#FFFFFF', padding: '20px', borderRadius: '8px', overflow: 'auto', fontSize: '12px', border: '1px solid rgba(0,0,0,0.06)' }}>
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px', padding: '10px 20px', background: '#D4A053', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { setConnected, setStatus, setPortfolio, setPositions, setStrategies, setRiskParams, setRiskExposure } = useTradingStore();
  const { checkAuthStatus, isAuthenticated, authType } = useAuth();
  useWebSocket();

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Start ACES polling when authenticated
  const startAcesPolling = useAcesStore(s => s.startPolling);
  useEffect(() => {
    const canFetch = isAuthenticated || authType === 'dev_mode';
    if (!canFetch) return;
    return startAcesPolling();
  }, [isAuthenticated, authType, startAcesPolling]);

  // Fetch data when authenticated or in dev mode
  useEffect(() => {
    const canFetch = isAuthenticated || authType === 'dev_mode';
    if (!canFetch) return;

    api.getHealth()
      .then(() => setConnected(true))
      .catch(err => console.error('Health check failed:', err));

    api.getStatus().then(setStatus).catch(console.error);
    api.getPortfolio().then(setPortfolio).catch(console.error);
    api.getPositions().then(setPositions).catch(console.error);
    api.getStrategies().then(setStrategies).catch(console.error);
    api.getRisk().then(data => {
      if (data.params) setRiskParams(data.params);
      if (data.current_exposure) setRiskExposure(data.current_exposure);
    }).catch(console.error);
  }, [isAuthenticated, authType, setConnected, setStatus, setPortfolio, setPositions, setStrategies, setRiskParams, setRiskExposure]);

  return (
    <div className="min-h-screen bg-background text-text-primary" style={{ background: '#F8F6F1' }}>
      <Outlet />
      <AuthModal />
    </div>
  );
}

// Wrap App with ErrorBoundary
export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
